import React, { useState, useEffect, useMemo } from 'react';
import { Eye, Settings, Filter, GripVertical } from 'lucide-react';
import { formatDate } from '../utils/dateFormatting';
import { Table, Badge, Button, Popover, Checkbox, SearchBar, Dropdown, FilterChip } from './ui';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Column Item Component
const SortableColumnItem = ({ column, checked, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        backgroundColor: isDragging ? 'var(--dark-elevated)' : 'transparent',
        borderRadius: 'var(--radius-md)',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'var(--gray-400)',
          padding: '4px',
        }}
      >
        <GripVertical size={16} />
      </div>
      <Checkbox
        label={column.label}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
};

const ModelQuotesTable = ({ quotes = [], onRowClick, hidePricing = false }) => {
  console.log('ModelQuotesTable received quotes:', quotes?.length || 0, 'items');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Filter states
  const [filters, setFilters] = useState({
    quoteStatus: '',
    state: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Default to 20
  const [totalPages, setTotalPages] = useState(1);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('model-quotes-visible-columns');
    const defaultColumns = {
      action: true,
      projectName: true,
      companyName: true,
      clientName: true,
      quoteStatus: true,
      price: !hidePricing,
      dateQuoted: true,
      quoteDueDate: true,
      state: true,
      isRevision: false,
      structureCount: false,
      email: false,
      name: false,
      erosionSurfaceDueDate: false,
      advancedUtility: false
    };

    const columns = saved ? JSON.parse(saved) : defaultColumns;

    // If hidePricing is true, force price column to be hidden
    if (hidePricing) {
      columns.price = false;
    }

    return columns;
  });

  // Column order state with drag-and-drop
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('model-quotes-column-order');
    return saved ? JSON.parse(saved) : [];
  });

  // Column settings popover state
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Auto-resize state
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(() => {
    const saved = localStorage.getItem('model-quotes-auto-resize');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters.quoteStatus, filters.state]);

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem('model-quotes-visible-columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Save column order to localStorage
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('model-quotes-column-order', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  // Save auto-resize to localStorage
  useEffect(() => {
    localStorage.setItem('model-quotes-auto-resize', JSON.stringify(autoResizeEnabled));
  }, [autoResizeEnabled]);

  const formatPrice = (price) => {
    if (!price) return '—';
    // If price is already formatted with $, return as is
    if (price.toString().includes('$')) return price;
    // Otherwise format it
    const numPrice = parseFloat(price.toString().replace(/[^0-9.-]/g, ''));
    if (isNaN(numPrice)) return price;
    return `$${numPrice.toLocaleString()}`;
  };

  const formatBoolean = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
  };

  // Get unique values for filter dropdowns
  const fieldOptions = useMemo(() => {
    const quoteStatuses = new Set();
    const states = new Set();

    quotes.forEach(quote => {
      if (quote.quote_status) quoteStatuses.add(quote.quote_status);
      if (quote.state) states.add(quote.state);
    });

    return {
      quoteStatus: Array.from(quoteStatuses).sort(),
      state: Array.from(states).sort(),
    };
  }, [quotes]);

  // Define all columns for the Table component
  const allColumns = useMemo(() => [
    {
      id: 'action',
      label: '',
      field: 'id',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      searchable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onRowClick) onRowClick(row);
          }}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
            e.currentTarget.style.color = 'var(--cta-blue)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="View quote details"
        >
          <Eye size={20} />
        </button>
      ),
    },
    {
      id: 'projectName',
      label: 'Project Name',
      field: 'project_name',
      width: 200,
      minWidth: 150,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'companyName',
      label: 'Company',
      field: 'company_name',
      width: 150,
      minWidth: 120,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'clientName',
      label: 'Client',
      field: 'client_name',
      width: 150,
      minWidth: 120,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'quoteStatus',
      label: 'Quote Status',
      field: 'quote_status',
      width: 130,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => {
        const variant =
          value === 'Won' ? 'success' :
          value === 'Lost' ? 'danger' :
          value === 'Pending' ? 'warning' :
          'neutral';

        return (
          <Badge variant={variant} size="sm">
            {value || 'Unknown'}
          </Badge>
        );
      },
    },
    {
      id: 'price',
      label: 'Price',
      field: 'price',
      width: 120,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => formatPrice(value),
    },
    {
      id: 'dateQuoted',
      label: 'Date Quoted',
      field: 'date_quoted',
      width: 120,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => formatDate(value),
    },
    {
      id: 'quoteDueDate',
      label: 'Due Date',
      field: 'quote_due_date',
      width: 120,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => formatDate(value),
    },
    {
      id: 'state',
      label: 'State',
      field: 'state',
      width: 80,
      minWidth: 60,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'isRevision',
      label: 'Revision',
      field: 'is_revision',
      width: 100,
      minWidth: 80,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => formatBoolean(value),
    },
    {
      id: 'structureCount',
      label: 'Structures',
      field: 'structure_count',
      width: 100,
      minWidth: 80,
      sortable: true,
      resizable: true,
      align: 'center',
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'email',
      label: 'Email',
      field: 'email',
      width: 200,
      minWidth: 150,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'name',
      label: 'Contact Name',
      field: 'name',
      width: 150,
      minWidth: 120,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'erosionSurfaceDueDate',
      label: 'Erosion Due',
      field: 'erosion_surface_due_date',
      width: 130,
      minWidth: 110,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => formatDate(value),
    },
    {
      id: 'advancedUtility',
      label: 'Adv. Utility',
      field: 'advanced_utility',
      width: 120,
      minWidth: 100,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => formatBoolean(value),
    },
  ], [onRowClick]);

  // Initialize column order if empty
  useEffect(() => {
    if (columnOrder.length === 0 && allColumns.length > 0) {
      const defaultOrder = allColumns.map(col => col.id);
      setColumnOrder(defaultOrder);
      localStorage.setItem('model-quotes-column-order', JSON.stringify(defaultOrder));
    }
  }, [allColumns, columnOrder.length]);

  // Filter and search quotes
  const filteredAndSortedQuotes = useMemo(() => {
    let filtered = quotes;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(quote =>
        quote.project_name?.toLowerCase().includes(searchLower) ||
        quote.company_name?.toLowerCase().includes(searchLower) ||
        quote.client_name?.toLowerCase().includes(searchLower) ||
        quote.email?.toLowerCase().includes(searchLower) ||
        quote.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.quoteStatus) {
      filtered = filtered.filter(quote => quote.quote_status === filters.quoteStatus);
    }

    // Apply state filter
    if (filters.state) {
      filtered = filtered.filter(quote => quote.state === filters.state);
    }

    return filtered;
  }, [quotes, searchTerm, filters]);

  // Pagination: Calculate total pages and get current page data
  const displayedQuotes = useMemo(() => {
    const totalFiltered = filteredAndSortedQuotes.length;
    const calculatedTotalPages = pageSize === 999999 ? 1 : Math.ceil(totalFiltered / pageSize);

    // Update total pages if it changed
    if (calculatedTotalPages !== totalPages) {
      setTotalPages(calculatedTotalPages);
    }

    // Reset to page 1 if current page is beyond total pages
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }

    // Return all if "All" is selected
    if (pageSize === 999999) {
      return filteredAndSortedQuotes;
    }

    // Return current page slice
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedQuotes.slice(startIndex, endIndex);
  }, [filteredAndSortedQuotes, currentPage, pageSize, totalPages]);

  // Get visible columns for table display with custom ordering
  const columns = useMemo(() => {
    // If no custom order, use default allColumns order
    if (columnOrder.length === 0) {
      return allColumns.filter(col => visibleColumns[col.id]);
    }

    // Apply custom order
    const orderedColumns = columnOrder
      .map(colId => allColumns.find(col => col.id === colId))
      .filter(col => col && visibleColumns[col.id]);

    return orderedColumns;
  }, [allColumns, columnOrder, visibleColumns]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  const handleRowClick = (quote) => {
    if (onRowClick) {
      onRowClick(quote);
    }
  };

  return (
    <div>
      {/* Active Filter Chips */}
      {(filters.quoteStatus || filters.state) && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-4)',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--gray-400)',
            fontWeight: 'var(--font-medium)'
          }}>
            Active filters:
          </span>
          {filters.quoteStatus && (
            <FilterChip
              label={`Status: ${filters.quoteStatus}`}
              onRemove={() => setFilters({ ...filters, quoteStatus: '' })}
            />
          )}
          {filters.state && (
            <FilterChip
              label={`State: ${filters.state}`}
              onRemove={() => setFilters({ ...filters, state: '' })}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ quoteStatus: '', state: '' })}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Table with integrated toolbar */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '85vh',
      }}>
        {/* Search Bar and Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search quotes..."
              size="sm"
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            {/* Filters Popover */}
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowFilters(!showFilters);
                    setShowColumnSettings(false);
                  }}
                >
                  <Filter size={16} />
                  Filters
                </Button>
              }
              position="bottom-right"
              width="400px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)'
                }}>
                  Filter Quotes
                </h3>

                {/* Quote Status Filter */}
                <Dropdown
                  label="Quote Status"
                  value={filters.quoteStatus}
                  options={[
                    { value: '', label: 'All statuses' },
                    ...fieldOptions.quoteStatus.map(status => ({ value: status, label: status }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, quoteStatus: value })}
                  placeholder="All statuses"
                  size="md"
                />

                {/* State Filter */}
                <Dropdown
                  label="State"
                  value={filters.state}
                  options={[
                    { value: '', label: 'All states' },
                    ...fieldOptions.state.map(state => ({ value: state, label: state }))
                  ]}
                  onChange={(value) => setFilters({ ...filters, state: value })}
                  placeholder="All states"
                  size="md"
                />

                {/* Footer Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: 'var(--space-3)',
                  borderTop: '1px solid var(--gray-700)'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters({ quoteStatus: '', state: '' })}
                  >
                    Clear all
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

            {/* Column Settings Popover */}
            <Popover
              isOpen={showColumnSettings}
              onClose={() => setShowColumnSettings(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowColumnSettings(!showColumnSettings);
                    setShowFilters(false);
                  }}
                >
                  <Settings size={16} />
                  Columns
                </Button>
              }
              position="bottom-right"
              width="520px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Header with count */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)'
                  }}>
                    Toggle Columns
                  </h3>
                  <span style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-400)',
                    backgroundColor: 'var(--dark-elevated)',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontWeight: 'var(--font-medium)'
                  }}>
                    {Object.values(visibleColumns).filter(Boolean).length} / {Object.keys(visibleColumns).length} visible
                  </span>
                </div>

                {/* Quick Actions */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  paddingBottom: 'var(--space-3)',
                  borderBottom: '1px solid var(--gray-700)',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allVisible = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allVisible[key] = true;
                      });
                      setVisibleColumns(allVisible);
                      localStorage.setItem('model-quotes-visible-columns', JSON.stringify(allVisible));
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allHidden = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allHidden[key] = false;
                      });
                      setVisibleColumns(allHidden);
                      localStorage.setItem('model-quotes-visible-columns', JSON.stringify(allHidden));
                    }}
                  >
                    Deselect All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const defaultOrder = allColumns.map(col => col.id);
                      setColumnOrder(defaultOrder);
                      localStorage.setItem('model-quotes-column-order', JSON.stringify(defaultOrder));
                    }}
                  >
                    Reset Order
                  </Button>
                </div>

                {/* Column Checkboxes with Drag & Drop */}
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: 'var(--space-2)'
                }}>
                  {/* Visible Columns (Checked) */}
                  <div style={{
                    marginBottom: 'var(--space-3)'
                  }}>
                    <div style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-400)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 'var(--space-2)',
                      paddingLeft: 'var(--space-2)'
                    }}>
                      Visible Columns
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columnOrder.filter(id => id !== 'action' && visibleColumns[id])}
                        strategy={verticalListSortingStrategy}
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-1)'
                        }}>
                          {columnOrder
                            .filter(id => id !== 'action' && visibleColumns[id])
                            .map(colId => {
                              const col = allColumns.find(c => c.id === colId);
                              if (!col) return null;
                              return (
                                <SortableColumnItem
                                  key={col.id}
                                  column={col}
                                  checked={visibleColumns[col.id]}
                                  onChange={(e) => {
                                    const newVisibleColumns = {
                                      ...visibleColumns,
                                      [col.id]: e.target.checked
                                    };
                                    setVisibleColumns(newVisibleColumns);
                                    localStorage.setItem('model-quotes-visible-columns', JSON.stringify(newVisibleColumns));
                                  }}
                                />
                              );
                            })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>

                  {/* Hidden Columns (Unchecked) */}
                  {columnOrder.filter(id => id !== 'action' && !visibleColumns[id]).length > 0 && (
                    <div style={{
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <div style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-400)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 'var(--space-2)',
                        paddingLeft: 'var(--space-2)'
                      }}>
                        Hidden Columns
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={columnOrder.filter(id => id !== 'action' && !visibleColumns[id])}
                          strategy={verticalListSortingStrategy}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-1)'
                          }}>
                            {columnOrder
                              .filter(id => id !== 'action' && !visibleColumns[id])
                              .map(colId => {
                                const col = allColumns.find(c => c.id === colId);
                                if (!col) return null;
                                return (
                                  <SortableColumnItem
                                    key={col.id}
                                    column={col}
                                    checked={visibleColumns[col.id]}
                                    onChange={(e) => {
                                      const newVisibleColumns = {
                                        ...visibleColumns,
                                        [col.id]: e.target.checked
                                      };
                                      setVisibleColumns(newVisibleColumns);
                                      localStorage.setItem('model-quotes-visible-columns', JSON.stringify(newVisibleColumns));
                                    }}
                                  />
                                );
                              })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>
              </div>
            </Popover>

            {/* Auto-resize toggle - FIXED: secondary when enabled, ghost when disabled */}
            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div style={{
          backgroundColor: 'var(--dark-surface)',
          overflowX: 'auto',
          overflowY: 'auto',
          flex: 1,
          minWidth: 0,
        }}>
          <Table
            data={displayedQuotes}
            columns={columns}
            sortable={true}
            searchable={false}
            resizable={true}
            paginated={false}
            storageKey="model-quotes-table"
            autoResizeOnDataChange={autoResizeEnabled}
            showAutoSizeButton={false}
            onManualResize={handleManualResize}
            emptyMessage={
              searchTerm || filters.quoteStatus || filters.state
                ? 'No quotes found matching your search or filters'
                : 'No quotes found'
            }
            onRowClick={handleRowClick}
            variant="dense"
            stickyHeader={true}
          />
        </div>

        {/* Custom Pagination - Matching ClientsTable */}
        {filteredAndSortedQuotes.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                {pageSize === 999999 ? (
                  `Showing all ${filteredAndSortedQuotes.length} quote${filteredAndSortedQuotes.length !== 1 ? 's' : ''}`
                ) : (
                  `Page ${currentPage} of ${totalPages} (${filteredAndSortedQuotes.length} quote${filteredAndSortedQuotes.length !== 1 ? 's' : ''})`
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                  Show:
                </span>
                <Button
                  variant={pageSize === 20 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(20)}
                >
                  20
                </Button>
                <Button
                  variant={pageSize === 50 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(50)}
                >
                  50
                </Button>
                <Button
                  variant={pageSize === 999999 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(999999)}
                >
                  All
                </Button>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </Button>
                    );
                  }

                  return pages;
                })()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelQuotesTable;
